import { useState } from "react";
import { Link } from "react-router-dom";
import { GlassCard, GlassCardContent, GlassCardHeader } from "@/components/ui/glass-card";
import { Badge } from "@/components/ui/badge";
import { Edit, Trash2, Eye, HardDrive, Users } from "lucide-react";
import { TagIcon } from "./TagIcon";
import { CustomerBadge } from "@/components/ui/customer-badge";
import { getCustomerNames } from "@/utils/customerLookup";
import { useTagResourceCounts } from "@/hooks/useTagResourceCounts";
import { ActionsDropdown, ActionItem } from "@/components/ui/actions-dropdown";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface Tag {
  id: string;
  name: string;
  description: string;
  type: string;
  color: string;
  icon?: string;
  criteria: any;
  created_at: string;
  created_by: string;
  customers: string[];
}

interface TagCardProps {
  tag: Tag;
  onDelete: (id: string) => void;
}

const formatCriteria = (criteria: any) => {
  if (!criteria) return "N/A";
  
  try {
    const logic = criteria.and ? "AND" : "OR";
    const conditions = criteria.and || criteria.or || [];
    
    if (conditions.length === 0) return "No conditions";
    
    return `${logic}: ${conditions.length} condition${conditions.length > 1 ? 's' : ''}`;
  } catch {
    return "Invalid criteria";
  }
};

export const TagCard = ({ tag, onDelete }: TagCardProps) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const { counts, isLoading: countsLoading } = useTagResourceCounts(tag.id);
  const customerNames = getCustomerNames(tag.customers || []);
  
  const actions: ActionItem[] = [
    {
      label: "View",
      icon: Eye,
      onClick: () => window.location.href = `/tags/${tag.id}`,
    },
    {
      label: "Edit",
      icon: Edit,
      onClick: () => {
        // TODO: Implement edit functionality
        console.log("Edit tag:", tag.id);
      },
    },
    {
      label: "Delete",
      icon: Trash2,
      onClick: () => setShowDeleteDialog(true),
      destructive: true,
    },
  ];
  
  return (
    <>
      <GlassCard variant="default">
        <GlassCardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-3">
              <TagIcon
                iconName={tag.icon}
                color={tag.color}
                size={20}
              />
              <div className="flex-1 min-w-0">
                <Link 
                  to={`/tags/${tag.id}`}
                  className="font-semibold text-foreground hover:text-primary transition-colors truncate block"
                >
                  {tag.name}
                </Link>
                <Badge 
                  variant={tag.type === "static" ? "secondary" : "default"}
                  className="mt-1"
                >
                  {tag.type}
                </Badge>
              </div>
            </div>
            <ActionsDropdown actions={actions} />
          </div>
        </GlassCardHeader>
        
        <GlassCardContent className="pt-0">
          <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
            {tag.description || "No description provided"}
          </p>
          
          {/* Customer Badges */}
          <div className="mb-3">
            <div className="flex flex-wrap gap-1">
              {customerNames.length > 0 ? (
                customerNames.map((customerName, index) => (
                  <CustomerBadge key={index} customer={customerName} />
                ))
              ) : (
                <Badge variant="outline" className="text-muted-foreground">
                  No customers
                </Badge>
              )}
            </div>
          </div>

          {/* Resource Counts */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="flex items-center gap-1 text-xs">
              <HardDrive className="h-3 w-3 text-muted-foreground" />
              <span className="text-muted-foreground">Devices:</span>
              <span className="font-medium">
                {countsLoading ? "..." : counts.devices}
              </span>
            </div>
            <div className="flex items-center gap-1 text-xs">
              <Users className="h-3 w-3 text-muted-foreground" />
              <span className="text-muted-foreground">Resources:</span>
              <span className="font-medium">
                {countsLoading ? "..." : counts.total}
              </span>
            </div>
          </div>
          
          <div className="space-y-2">
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">Criteria:</span>
              <span className="font-medium">{formatCriteria(tag.criteria)}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">Created:</span>
              <span className="font-medium">
                {new Date(tag.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>
        </GlassCardContent>
      </GlassCard>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Tag</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the tag <strong>"{tag.name}"</strong>? 
              This action cannot be undone.
              {!countsLoading && counts.total > 0 && (
                <div className="mt-3 p-3 bg-muted rounded-md text-sm">
                  <p className="font-medium mb-2">This tag is currently associated with:</p>
                  <ul className="space-y-1">
                    {counts.devices > 0 && <li>• {counts.devices} device{counts.devices !== 1 ? 's' : ''}</li>}
                    {counts.profiles > 0 && <li>• {counts.profiles} profile{counts.profiles !== 1 ? 's' : ''}</li>}
                    {counts.applications > 0 && <li>• {counts.applications} application{counts.applications !== 1 ? 's' : ''}</li>}
                    {counts.configurations > 0 && <li>• {counts.configurations} configuration{counts.configurations !== 1 ? 's' : ''}</li>}
                  </ul>
                  <p className="mt-2 text-muted-foreground">
                    These resources will no longer be associated with this tag.
                  </p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                onDelete(tag.id);
                setShowDeleteDialog(false);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};